import * as React from 'react';
import { cn } from '../utils/cn';

export interface SwipeActionButton {
  text: React.ReactNode;
  onPress?: () => void;
  style?: React.CSSProperties;
  className?: string;
}

export interface SwipeActionProps {
  /** Buttons shown when swiping right-to-left */
  right?: SwipeActionButton[];
  /** Buttons shown when swiping left-to-right */
  left?: SwipeActionButton[];
  /** Auto-close after action press */
  autoClose?: boolean;
  /** Disable swiping */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  /** Main content */
  children?: React.ReactNode;
}

export function SwipeAction({
  right = [],
  left = [],
  autoClose = true,
  disabled = false,
  className,
  style,
  children,
}: SwipeActionProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [offsetX, setOffsetX] = React.useState(0);
  const [startX, setStartX] = React.useState(0);
  const [isSwiping, setIsSwiping] = React.useState(false);

  const rightWidth = right.length * 80;
  const leftWidth = left.length * 80;

  const handleTouchStart = (e: React.TouchEvent) => {
    if (disabled) return;
    setStartX(e.touches[0].clientX);
    setIsSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping || disabled) return;
    const diff = e.touches[0].clientX - startX;
    // Clamp offset within bounds
    const newOffset = Math.max(-rightWidth, Math.min(leftWidth, diff + offsetX));
    setOffsetX(newOffset);
    setStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!isSwiping) return;
    setIsSwiping(false);
    // Snap to open or closed position
    if (offsetX < -rightWidth / 2) {
      setOffsetX(-rightWidth);
    } else if (offsetX > leftWidth / 2) {
      setOffsetX(leftWidth);
    } else {
      setOffsetX(0);
    }
  };

  const close = () => setOffsetX(0);

  const handleActionPress = (action: SwipeActionButton) => {
    action.onPress?.();
    if (autoClose) close();
  };

  return (
    <div
      ref={containerRef}
      className={cn('relative overflow-hidden', className)}
      style={style}
    >
      {/* Left actions */}
      {left.length > 0 && (
        <div className="absolute inset-y-0 left-0 flex">
          {left.map((action, i) => (
            <button
              key={i}
              onClick={() => handleActionPress(action)}
              className={cn(
                'flex items-center justify-center w-20 h-full',
                'bg-accent text-white text-xs font-body font-medium',
                '!tracking-normal !normal-case !rounded-none',
                action.className
              )}
              style={action.style}
            >
              {action.text}
            </button>
          ))}
        </div>
      )}

      {/* Right actions */}
      {right.length > 0 && (
        <div className="absolute inset-y-0 right-0 flex">
          {right.map((action, i) => (
            <button
              key={i}
              onClick={() => handleActionPress(action)}
              className={cn(
                'flex items-center justify-center w-20 h-full',
                'bg-destructive text-white text-xs font-body font-medium',
                '!tracking-normal !normal-case !rounded-none',
                action.className
              )}
              style={action.style}
            >
              {action.text}
            </button>
          ))}
        </div>
      )}

      {/* Main content */}
      <div
        className="relative bg-white transition-transform duration-fast ease-smooth"
        style={{
          transform: `translateX(${isSwiping ? offsetX : offsetX}px)`,
          transition: isSwiping ? 'none' : undefined,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>
    </div>
  );
}
